<template>
  <div class="finding-card" :class="'finding-severity-' + severity" role="article" :aria-label="title">
    <div class="finding-header">
      <span class="finding-severity-badge" :style="{ backgroundColor: severityColor, color: '#ffffff' }">
        {{ severityLabel }}
      </span>
      <span class="finding-category">{{ categoryLabel }}</span>
      <span class="finding-confidence" :title="'Confidence: ' + confidenceLabel">Conf: {{ confidenceLabel }}</span>
    </div>
    <h4 class="finding-title">{{ title }}</h4>
    <p v-if="description" class="finding-description">{{ description }}</p>
    <div class="finding-meta">
      <span class="finding-affected">
        <strong>{{ affectedCount }}</strong> {{ affectedLabel }}
      </span>
      <span v-if="impactText" class="finding-impact">{{ impactText }}</span>
    </div>
    <div v-if="evidencePreview" class="finding-evidence">
      <span class="evidence-label">Evidence:</span>
      <code class="evidence-text">{{ evidencePreview }}</code>
    </div>
    <div class="finding-footer">
      <div class="finding-dates">
        <span>First detected: {{ firstDetected }}</span>
        <span>Last evaluated: {{ lastEvaluated }}</span>
        <span v-if="ruleVersion">Rule: {{ ruleId }} v{{ ruleVersion }}</span>
      </div>
      <div class="finding-actions">
        <button v-if="primaryAction" class="btn-primary" @click="handlePrimaryAction">{{ primaryAction }}</button>
        <button v-if="secondaryActions && secondaryActions.length" class="btn-secondary" @click="handleSecondaryActions">
          {{ secondaryLabel }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getSeverityInfo, getConfidenceLabel } from '@/services/entra-utils.js';

export default {
  name: 'FindingCard',
  props: {
    title: { type: String, required: true },
    description: { type: String, default: '' },
    severity: { type: String, default: 'medium' },
    confidence: { type: String, default: 'medium' },
    categoryLabel: { type: String, default: '' },
    affectedCount: { type: [String, Number], default: 0 },
    affectedLabel: { type: String, default: 'objects affected' },
    impactText: { type: String, default: '' },
    evidencePreview: { type: String, default: '' },
    firstDetected: { type: String, default: '' },
    lastEvaluated: { type: String, default: '' },
    ruleId: { type: String, default: '' },
    ruleVersion: { type: String, default: '' },
    primaryAction: { type: String, default: '' },
    secondaryActions: { type: Array, default: function() { return []; } },
    secondaryLabel: { type: String, default: 'More actions' }
  },
  computed: {
    severityInfo: function() { return getSeverityInfo(this.severity); },
    severityColor: function() { return this.severityInfo.color; },
    severityLabel: function() { return this.severityInfo.label; },
    confidenceLabel: function() { return getConfidenceLabel(this.confidence); }
  },
  methods: {
    handlePrimaryAction: function() { this.$emit('primary-action'); },
    handleSecondaryActions: function() { this.$emit('secondary-action'); }
  }
};
</script>

<style scoped>
.finding-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-left-width: 4px;
  border-radius: 8px;
  padding: 16px;
}
.finding-severity-critical { border-left-color: #dc2626; }
.finding-severity-high { border-left-color: #ea580c; }
.finding-severity-medium { border-left-color: #ca8a04; }
.finding-severity-low { border-left-color: #2563eb; }
.finding-severity-informational { border-left-color: #6b7280; }

.finding-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.finding-severity-badge {
  font-size: 0.6875rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;
  text-transform: uppercase;
}

.finding-category {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
}

.finding-confidence {
  font-size: 0.6875rem;
  color: #94a3b8;
  margin-left: auto;
}

.finding-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
}

.finding-description {
  font-size: 0.8125rem;
  color: #475569;
  margin: 0 0 8px 0;
}

.finding-meta {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.finding-affected {
  font-size: 0.8125rem;
  color: #334155;
}

.finding-impact {
  font-size: 0.8125rem;
  color: #16a34a;
  font-weight: 600;
}

.finding-evidence {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 8px;
  margin-bottom: 12px;
}

.evidence-label {
  font-size: 0.6875rem;
  color: #94a3b8;
  font-weight: 600;
  display: block;
  margin-bottom: 4px;
}

.evidence-text {
  font-size: 0.75rem;
  color: #475569;
  word-break: break-all;
  background: none;
  padding: 0;
}

.finding-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}

.finding-dates {
  font-size: 0.6875rem;
  color: #94a3b8;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.finding-actions {
  display: flex;
  gap: 8px;
}

.btn-primary {
  background: #3b82f6;
  color: #ffffff;
  border: none;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  font-family: 'Inter', sans-serif;
}
.btn-primary:hover { background: #2563eb; }

.btn-secondary {
  background: transparent;
  color: #475569;
  border: 1px solid #cbd5e1;
  padding: 5px 13px;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Inter', sans-serif;
}
.btn-secondary:hover { background: #f1f5f9; border-color: #94a3b8; }
</style>
